import { useState } from 'react';
import { Edit2, Trash2, Plus, Check, X } from 'lucide-react';
import { SwotAnalysisState, SwotItem } from '../../interface/ReasearchData';
import { useDispatch } from 'react-redux';
import { deleteSwotItem, setSwotItem, updateSwotItem } from '../../store/ResearchDataSlice';

interface SwotQuadrantProps {
  title: string;
  items: SwotItem[];
  itemKey: keyof SwotAnalysisState;
  color: 'green' | 'red' | 'blue' | 'orange';
}

export function SwotQuadrant({
  title,
  items,
  itemKey,
  color
}: SwotQuadrantProps) {
  const dispatch = useDispatch();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newItemText, setNewItemText] = useState('');

  const colorClasses = {
    green: 'bg-green-50 border-green-500 text-green-800',
    red: 'bg-red-50 border-red-500 text-red-800',
    blue: 'bg-blue-50 border-blue-500 text-blue-800',
    orange: 'bg-orange-50 border-orange-500 text-orange-800'
  };
  const darkColorClasses = {
    green: 'bg-green-900/20 border-green-500/50 text-green-200',
    red: 'bg-red-900/20 border-red-500/50 text-red-200',
    blue: 'bg-blue-900/20 border-blue-500/50 text-blue-200',
    orange: 'bg-orange-900/20 border-orange-500/50 text-orange-200'
  };
  const titleColorClasses = {
    green: 'text-green-700',
    red: 'text-red-700',
    blue: 'text-blue-700',
    orange: 'text-orange-700'
  };
  const darkTitleColorClasses = {
    green: 'text-green-300',
    red: 'text-red-300',
    blue: 'text-blue-300',
    orange: 'text-orange-300'
  };

  const handleEdit = (id: number, currentText: string) => {
    setEditingId(id);
    setEditText(currentText);
  };

  const handleSaveEdit = (id: number) => {
    if (editText.trim()) {
      dispatch(updateSwotItem({
        itemKey,
        id,
        text: editText
      }));
    }
    setEditingId(null);
    setEditText('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleDelete = (id: number) => {
    dispatch(deleteSwotItem({
      itemKey,
      id
    }));
  };

  const handleAddItem = () => {
    if (newItemText.trim()) {
      const newId = items.length > 0 ? Math.max(...items.map(item => item.id)) + 1 : 1;
      const item: SwotItem = {
        id: newId,
        text: newItemText
      };
      dispatch(setSwotItem({ itemKey, item: item }));
      setNewItemText('');
    }
    setIsAdding(false);
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setNewItemText('');
  };

  const microcopyByTitle: Record<string, string> = {
    Strengths: 'Internal advantages that help you win (brand, tech, distribution).',
    Weaknesses: 'Internal gaps that slow you down (costs, product, execution).',
    Opportunities: 'External tailwinds to capture (market, tech shifts, regulation).',
    Threats: 'External risks to defend against (competition, regulation, disruption).',
  };

  return (
    <div className={`p-6 rounded-lg border h-full relative light-theme:${colorClasses[color]} dark-theme:${darkColorClasses[color]} bg-theme-secondary border-theme-tertiary`}>
      <h3 className={`text-2xl font-bold text-center light-theme:${titleColorClasses[color]} dark-theme:${darkTitleColorClasses[color]} text-theme-primary`}>
        {title}
      </h3>
      {/* Compact add button in header area to save space */}
      {!isAdding && (
        <button
          onClick={() => setIsAdding(true)}
          className="absolute top-3 right-3 p-1.5 rounded border btn btn-sm active border-theme-tertiary bg-theme-tertiary text-theme-muted hover:text-theme-primary hover:bg-theme-tertiary/70"
          title="Professional"
          aria-label="Professional"
        >
          <Plus size={14} />
        </button>
      )}
      <p className="mt-1 mb-4 text-xs text-theme-muted text-center">{microcopyByTitle[title] || ''}</p>
      <ul className="space-y-3">
        {items?.map((item) => (
          <li key={item.id} className="flex items-start gap-2">
            {editingId === item.id ? (
              <>
                <span className="mt-1 flex-shrink-0 text-theme-primary">•</span>
                <div className="flex-1">
                  <input
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="w-full p-1 border rounded bg-theme-tertiary border-theme-tertiary text-theme-primary"
                    autoFocus
                  />
                  <div className="flex gap-2 mt-1">
                    <button onClick={() => handleSaveEdit(item.id)} className="p-1 bg-green-600 text-theme-primary rounded hover:bg-green-700">
                      <Check size={14} />
                    </button>
                    <button onClick={handleCancelEdit} className="p-1 bg-theme-tertiary text-theme-secondary rounded hover:bg-theme-tertiary/70 border border-theme-tertiary">
                      <X size={14} />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <span className="mt-1 flex-shrink-0 text-theme-primary">•</span>
                <div className="flex-1 break-words pr-2 text-theme-primary">{item.text}</div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => handleEdit(item.id, item.text)} className="p-1 text-theme-muted hover:text-blue-600" aria-label="Edit item">
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="p-1 text-theme-muted hover:text-red-600" aria-label="Delete item">
                    <Trash2 size={16} />
                  </button>
                </div>
              </>
            )}
          </li>
        ))}
        {isAdding && (
          <li className="flex items-start gap-2">
            <span className="mt-1 flex-shrink-0 text-theme-primary">•</span>
            <div className="flex-1">
              <input
                type="text"
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                className="w-full p-1 border rounded input-bg border-theme-tertiary text-theme-primary"
                placeholder="Enter new point"
                autoFocus
              />
              <div className="flex gap-2 mt-1">
                <button onClick={handleAddItem} className="p-1 btn btn-xs active text-theme-primary rounded hover:bg-green-700">
                  <Check size={14} />
                </button>
                <button onClick={handleCancelAdd} className="p-1 btn btn-xs bg-theme-tertiary text-theme-secondary rounded hover:bg-theme-tertiary/70 border border-theme-tertiary">
                  <X size={14} />
                </button>
              </div>
            </div>
          </li>
        )}
      </ul>
    </div>
  );
}